import { Target, Loader2 } from 'lucide-react'
import { useGlobalProgress } from '@/api/trpc'
import { getPerformanceTextColor } from '@/lib/financial-utils'
import { useCountUp } from '@/hooks/useCountUp'

/**
 * ProaResultGlobal - Displays global PROA achievement progress
 *
 * SECURITY: This component uses tRPC `progress.getGlobalProgress` which returns
 * ONLY percentages and status - never raw financial values. This ensures
 * directors and non-admin users cannot see actual revenue numbers.
 */
export function ProaResultGlobal() {
  const currentYear = new Date().getFullYear()

  // Use progress hook for global progress (returns only percentages, no raw values)
  const {
    data,
    isLoading,
    error,
  } = useGlobalProgress({ year: currentYear })

  // Animated percentage counter
  const animatedPercentage = useCountUp(data?.progress.achievementPercent || 0, { duration: 1500, decimals: 0 })

  // Show loading state
  if (isLoading) {
    return (
      <div>
        <div className="card h-2/5 flex items-center justify-center">
          <div className="text-center animate-fade-in-up">
            <Loader2 className="w-8 h-8 text-brand-primary animate-spin mx-auto mb-3" />
            <p className="text-brand-gray-light font-body">Carregando dados de performance...</p>
          </div>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div>
        <div className="card h-2/5 flex items-center justify-center">
          <div className="text-center animate-fade-in-up">
            <div className="text-red-400 mb-3">Erro ao carregar dados</div>
            <p className="text-brand-gray-light font-body text-sm">{error.message}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 px-4 py-2 bg-brand-primary text-white rounded-monumenta text-sm hover:bg-brand-primary/80 transition-colors"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      </div>
    )
  }

  const progress = data?.progress

  // Get performance color and background class based on achievement percentage
  const performanceColor = progress ? getPerformanceTextColor(progress.achievementPercent) : 'text-gray-400'
  const globalBgClass = performanceColor.replace('text-', 'bg-')

  // Get contextual message based on status from backend
  const getPerformanceMessage = (status: 'ahead' | 'on-track' | 'behind' | 'critical') => {
    switch (status) {
      case 'ahead': return { text: '🔥 Superando Metas!', color: 'text-success' }
      case 'on-track': return { text: '📈 No Caminho Certo', color: 'text-warning' }
      case 'behind': return { text: '⚡ Foco Necessário', color: 'text-yellow-500' }
      case 'critical': return { text: '🚨 Atenção Urgente', color: 'text-error' }
    }
  }

  const performanceMessage = progress ? getPerformanceMessage(progress.status) : null

  return (
    <div>
      {/* Global Progress Section - Takes about 40% of component height */}
      <div className={`card h-2/5 transition-shadow duration-300 ${progress && progress.status === 'ahead' ? 'shadow-glow-brand animate-glow' : ''}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-monumenta bg-brand-primary/10">
              <Target className="w-6 h-6 text-brand-primary" />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold text-white">PROA x Resultado</h2>
              <p className="text-sm text-brand-gray">Global - {progress?.totalClients || 0} clientes</p>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-3xl font-display font-bold ${performanceColor} transition-colors duration-500`}>
              {animatedPercentage}%
            </div>
            {performanceMessage && (
              <div className={`text-sm font-semibold ${performanceMessage.color} animate-fade-in mt-1`}>
                {performanceMessage.text}
              </div>
            )}
            <div className="text-xs text-brand-gray mt-1">
              Performance Global vs Meta
            </div>
          </div>
        </div>

        {/* Client breakdown stats */}
        {progress && (
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="text-center p-2 bg-success/10 rounded-monumenta">
              <div className="text-success font-bold">{progress.clientsAhead}</div>
              <div className="text-xs text-brand-gray">Acima</div>
            </div>
            <div className="text-center p-2 bg-warning/10 rounded-monumenta">
              <div className="text-warning font-bold">{progress.clientsOnTrack}</div>
              <div className="text-xs text-brand-gray">No Alvo</div>
            </div>
            <div className="text-center p-2 bg-error/10 rounded-monumenta">
              <div className="text-error font-bold">{progress.clientsBehind}</div>
              <div className="text-xs text-brand-gray">Abaixo</div>
            </div>
          </div>
        )}

        {/* Global Progress Bar */}
        <div className="relative w-full bg-brand-dark-lighter rounded-monumenta h-4 shadow-inner overflow-hidden">
          {/* Milestone markers */}
          {[25, 50, 75, 100].map((milestone) => (
            <div
              key={milestone}
              className="absolute top-0 bottom-0 w-px bg-brand-gray/30"
              style={{ left: `${milestone}%` }}
            />
          ))}
          <div
            className={`h-4 rounded-monumenta transition-all duration-1000 ease-out shadow-monumenta ${globalBgClass} relative overflow-hidden`}
            style={{ width: `${Math.min(animatedPercentage, 100)}%` }}
          >
            {/* Shimmer effect on progress bar */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"
                 style={{ backgroundSize: '200% 100%' }} />
          </div>
        </div>

        <div className="flex justify-between items-center text-xs text-brand-gray mt-2">
          <span>0%</span>
          <span>100%</span>
        </div>

        {/* As of date */}
        {data?.asOfDate && (
          <div className="text-xs text-brand-gray mt-2 text-right">
            Dados até: {new Date(data.asOfDate).toLocaleDateString('pt-BR')}
          </div>
        )}
      </div>
    </div>
  )
}
